/**
 * useNews — polls /api/news every 60 seconds and pushes items to the store.
 * Falls back gracefully when the API is unreachable.
 */

import { useEffect, useRef } from 'react';
import type { NewsItem } from '@arc/shared';
import { useTerminalStore } from '../stores/terminal.store.js';

const POLL_INTERVAL_MS = 60_000;

async function fetchNews(): Promise<NewsItem[]> {
  const res = await fetch('/api/news?limit=30', {
    signal: AbortSignal.timeout(10_000),
  });
  if (!res.ok) throw new Error(`News fetch ${res.status}`);
  const data = await res.json() as { items: NewsItem[] };
  return data.items ?? [];
}

export function useNews(): void {
  const setNews  = useTerminalStore(s => s.setNews);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    async function poll() {
      try {
        const items = await fetchNews();
        setNews(items);
      } catch {
        // API offline / network error — keep stale data, try again next cycle
      }
    }

    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [setNews]);
}
